import React, { useState } from "react"
import debounce from "debounce-promise"
import { useQueryClient } from "react-query"
import {
  Box, HStack, Input, InputGroup, InputLeftAddon, InputRightElement, ListItem, Spinner, Text,
  UnorderedList,
} from "@chakra-ui/react"
import { Controller } from "react-hook-form"
import * as api from "api/events"
import { setRenamedEvent } from "context/actions"
import { useCurrentUser } from "queries/users"
import { DioboxEvent } from "sharedTypes"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import Icon from "components/elements/Icon"
import { removeProtocolFromUrl } from "utilities/strings"
import { successToast } from "utilities/toasts"
import { LinkPrefixText, linkPrefixStyle } from "./LinkParts"

const debouncedValidateEventSlug = debounce(api.validateEventSlug, 250)

const HELP = "Letters, numbers and hyphens only."

type Status = { kind: "idle" | "checking" | "ok" | "error", message: string }

type Props = {
  event: DioboxEvent
  onHide: () => void
}

const formatDate = (iso?: string | null) => (iso
  ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  : "")

const cleanSlug = (value: string) => value
  .toLowerCase()
  .replace(/[\s_]+/g, "-")
  .replace(/[^a-z0-9-]/g, "")

const CurrentLink = ({ link }: { link: string }) => (
  <Text mb={4}>
    Current link:
    {" "}
    <Text as="span" fontFamily="mono" fontSize="sm" fontWeight="semibold">{link}</Text>
  </Text>
)

const ChangeEventLink = ({ event, onHide }: Props) => {
  const queryClient = useQueryClient()
  const currentUser = useCurrentUser()
  const [status, setStatus] = useState<Status>({ kind: "idle", message: HELP })
  const [transferFrom, setTransferFrom] = useState<string | null>(null)
  const rootUrl = event.urls?.root
  const currentLink = rootUrl ? removeProtocolFromUrl(rootUrl) : event.slug
  const allowedAt = event.slugRenameAllowedAt
  const locked = Boolean(allowedAt && new Date(allowedAt) > new Date())

  const refuse = (kind: Status["kind"], message: string) => {
    setStatus({ kind, message })
    setTransferFrom(null)

    return message
  }

  const validateSlug = async (slug: string) => {
    if (!slug) return refuse("idle", HELP)
    if (slug === event.slug) return refuse("idle", "This is the current link.")
    if (slug.length < 3) return refuse("error", "Please enter at least 3 characters.")
    if (/^-|-$/.test(slug)) return refuse("error", "Cannot start or end with a hyphen.")

    setStatus({ kind: "checking", message: "Checking availability…" })

    return debouncedValidateEventSlug(event.id, slug)
      .then(({ data: result }) => {
        if (!result.valid) return refuse("error", result.error?.message ?? "This link is not available.")

        setStatus({ kind: "ok", message: "Available" })
        setTransferFrom(result.transferFrom ?? null)

        return undefined
      })
      .catch(() => refuse("error", "Something went wrong."))
  }

  const onSubmit = (values: { slug: string }) => api
    .putEventSlug(event.id, values.slug)
    .then(({ data: updated }) => {
      if (currentUser) setRenamedEvent(queryClient, updated, currentUser)
      successToast({ title: "Event Link updated" })
      onHide()
    })
    .catch(() => {})

  if (locked) {
    return (
      <ModalForm title="Change Event Link" onClose={onHide} onSubmit={onHide} submitText="Close" hideCancel size="lg">
        <CurrentLink link={currentLink} />
        <HStack align="flex-start" spacing={3} bg="blue.50" borderWidth={1} borderColor="gray.300" borderRadius="md" p={4}>
          <Icon icon="info" size={4} color="blue.500" mt={1} />
          <Text>
            The Event Link was last changed on <b>{formatDate(event.slugChangedAt)}</b>.
            It can be changed again after <b>{formatDate(event.slugRenameAllowedAt)}</b>.
          </Text>
        </HStack>
      </ModalForm>
    )
  }

  const statusIcon = {
    checking: <Spinner size="sm" color="gray.500" />,
    ok: <Icon icon="checkmark" size={4} color="green.500" />,
  }[status.kind]
  const invalid = status.kind === "error"

  return (
    <ModalForm
      title="Change Event Link"
      onClose={onHide}
      initialValues={{ slug: "" }}
      onSubmit={onSubmit}
      submitText="Change Link"
      disableSubmitWhenNotDirty
      mode="onChange"
      size="lg"
    >
      {({ control, isValid }) => (
        <>
          <CurrentLink link={currentLink} />
          <Text as="label" htmlFor="new-event-link" display="block" fontWeight="semibold" mb={2}>New Link</Text>
          <Controller
            name="slug"
            control={control}
            rules={{ validate: validateSlug }}
            render={({ field }) => (
              <InputGroup>
                <InputLeftAddon {...linkPrefixStyle}>
                  <LinkPrefixText url={rootUrl} />
                </InputLeftAddon>
                <Input
                  {...field}
                  id="new-event-link"
                  aria-label="New Event Link"
                  autoComplete="off"
                  spellCheck={false}
                  fontFamily="mono"
                  fontSize="sm"
                  borderColor={invalid ? "red.500" : "gray.300"}
                  boxShadow={invalid ? "0 0 0 1px var(--chakra-colors-red-500)" : undefined}
                  fontWeight="semibold"
                  pe={10}
                  onChange={(e) => field.onChange(cleanSlug(e.target.value))}
                />
                {statusIcon && <InputRightElement>{statusIcon}</InputRightElement>}
              </InputGroup>
            )}
          />
          <Text fontSize="sm" minH={5} mt={1.5} color={invalid ? "red.500" : "gray.600"}>
            {status.message}
          </Text>
          {isValid && transferFrom && (
            <Text fontSize="sm" color="orange.600" mt={1}>
              This link is used by your event “{transferFrom}”. Changing moves it here,
              and “{transferFrom}” gets a new generated link.
            </Text>
          )}
          <Box bg="gray.50" borderWidth={1} borderColor="gray.200" borderRadius="md" px={4} py={3} mt={5} fontSize="sm">
            <Text fontWeight="semibold" mb={1}>Before changing</Text>
            <UnorderedList spacing={1}>
              <ListItem>
                The old link,
                {" "}
                <Text as="span" fontFamily="mono" fontWeight="semibold">{currentLink}</Text>
                , and all of the event&apos;s other links keep working:
                they redirect to the new link.
              </ListItem>
              <ListItem>
                The old name stays reserved to your account, so you can switch back to it later.
              </ListItem>
              <ListItem>
                {event.slugClaimed
                  ? "After this change, the Event Link can be changed again in 30 days."
                  : "Later changes are limited to one every 30 days."}
              </ListItem>
            </UnorderedList>
          </Box>
        </>
      )}
    </ModalForm>
  )
}

export default ChangeEventLink
